import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Clock,
  Play,
  Loader2,
  Download,
  TrendingDown,
  Zap,
  BarChart3,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DowntimePredictor = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predicting, setPredicting] = useState(false);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/downtime-prediction/latest`);
      setPrediction(response.data.prediction);
    } catch (error) {
      console.log('No downtime prediction yet');
    } finally {
      setLoading(false);
    }
  };

  const runPrediction = async () => {
    setPredicting(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/downtime-prediction/calculate`, {});
      setPrediction(response.data);
    } catch (error) {
      console.error('Error:', error);
      alert('Error calculating downtime. Ensure Phase 2 and SUM Digital Twin have been run first.');
    } finally {
      setPredicting(false);
    }
  };

  const getRAGColor = (rag) => {
    if (rag === 'GREEN') return 'bg-green-100 text-green-700 border-green-500';
    if (rag === 'AMBER') return 'bg-yellow-100 text-yellow-700 border-yellow-500';
    return 'bg-red-100 text-red-700 border-red-500';
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Downtime Predictor</h2>
            <p className="text-gray-600">Predict and optimize conversion downtime with Monte Carlo simulation</p>
            {prediction && prediction.sap_system_analyzed && prediction.sap_system_analyzed.name && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span className="text-gray-500">Analyzing System:</span>
                <span className="font-medium text-blue-700">{prediction.sap_system_analyzed.name}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  prediction.data_source === 'Real SAP System' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {prediction.data_source || 'Mock Data'}
                </span>
              </div>
            )}
          </div>
          <button 
            onClick={runPrediction}
            disabled={predicting}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
          >
            {predicting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Calculating...</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                <span>Calculate Downtime</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Empty State */}
      {!prediction && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Clock className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Downtime Prediction</h3>
          <p className="text-gray-600 mb-6">Calculate downtime estimates with risk bands and optimization scenarios</p>
          <button 
            onClick={runPrediction}
            className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 inline-flex items-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>Run Prediction</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      {prediction && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-5 gap-6">
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
              <Clock className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{prediction.summary.baseline_hours}h</div>
              <div className="text-red-100 text-sm">Baseline</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <BarChart3 className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{prediction.summary.p50_hours}h</div>
              <div className="text-blue-100 text-sm">P50 (Median)</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{prediction.summary.p80_hours}h</div>
              <div className="text-orange-100 text-sm">P80 (Conservative)</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <TrendingDown className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{prediction.summary.best_scenario_hours}h</div>
              <div className="text-green-100 text-sm">Best Scenario</div>
            </div>
            <div className={`rounded-xl p-6 shadow-lg border-2 ${getRAGColor(prediction.summary.rag_status)}`}>
              <Zap className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-2xl font-bold mb-2">{prediction.summary.rag_status}</div>
              <div className="text-sm opacity-80">RAG Status</div>
            </div>
          </div>

          {/* Within Acceptable Limits Badge */}
          {prediction.summary.within_acceptable ? (
            <div className="bg-green-50 border-2 border-green-500 rounded-xl p-4 flex items-center space-x-3">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <p className="text-green-700 font-semibold">
                P80 downtime ({prediction.summary.p80_hours}h) is within acceptable limits ({prediction.summary.max_acceptable_hours}h)
              </p>
            </div>
          ) : (
            <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <p className="text-red-700 font-semibold">
                P80 downtime ({prediction.summary.p80_hours}h) exceeds acceptable limits ({prediction.summary.max_acceptable_hours}h) - Optimization required
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'distribution', label: 'Risk Bands', icon: AlertTriangle },
                { id: 'scenarios', label: 'Optimization', icon: TrendingDown },
                { id: 'windows', label: 'Go-Live Windows', icon: Calendar },
                { id: 'narrative', label: 'AI Insights', icon: Sparkles }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Downtime Breakdown */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Baseline Downtime Breakdown</h4>
                    <div className="space-y-3">
                      {Object.entries(prediction.baseline_downtime.breakdown).map(([category, hours]) => {
                        const percentage = (hours / prediction.baseline_downtime.total_baseline_downtime_hours * 100).toFixed(1);
                        return (
                          <div key={category} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-gray-700 capitalize">
                                {category.replace(/_/g, ' ')}
                              </span>
                              <span className="text-sm font-bold text-gray-900">{hours}h ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Downtime Phases */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Downtime Phases</h4>
                    <div className="space-y-2">
                      {prediction.baseline_downtime.downtime_phases.map((phase, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <span className="text-sm font-semibold text-gray-900">{phase.phase_name}</span>
                          <span className="text-lg font-bold text-red-600">{phase.duration_hours}h</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Risk Bands Tab */}
              {activeTab === 'distribution' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-6">Downtime Risk Bands (Monte Carlo Simulation)</h3>
                    <div className="grid md:grid-cols-4 gap-4">
                      {[
                        { label: 'P10 (Optimistic)', value: prediction.downtime_distribution.p10, color: 'green' },
                        { label: 'P50 (Median)', value: prediction.downtime_distribution.p50, color: 'blue' },
                        { label: 'P80 (Conservative)', value: prediction.downtime_distribution.p80, color: 'orange' },
                        { label: 'P90 (Worst Case)', value: prediction.downtime_distribution.p90, color: 'red' }
                      ].map((band) => (
                        <div key={band.label} className="bg-white rounded-lg p-4 text-center">
                          <p className="text-sm text-gray-600 mb-2">{band.label}</p>
                          <p className={`text-3xl font-bold text-${band.color}-600`}>{band.value}h</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 bg-white rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Risk Multiplier Applied:</p>
                      <p className="text-xl font-bold text-gray-900">{prediction.downtime_distribution.risk_multiplier_applied}x</p>
                      <p className="text-xs text-gray-500 mt-2">
                        Based on {prediction.downtime_distribution.risk_multiplier_applied > 1 ? 'elevated' : 'normal'} risk from Unicode/add-ons/blockers
                      </p>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Planning Recommendations</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          Use <span className="font-bold">P50 ({prediction.downtime_distribution.p50}h)</span> for internal planning and team communications
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-orange-600 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          Use <span className="font-bold">P80 ({prediction.downtime_distribution.p80}h)</span> for business communications and stakeholder commitments
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          Reserve additional buffer ({(prediction.downtime_distribution.p90 - prediction.downtime_distribution.p80).toFixed(1)}h) for contingency planning
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Optimization Scenarios Tab */}
              {activeTab === 'scenarios' && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Downtime Reduction Scenarios</h4>
                    <p className="text-sm text-gray-600">
                      Explore optimization levers to reduce conversion downtime
                    </p>
                  </div>

                  <div className="space-y-4">
                    {prediction.downtime_scenarios.map((scenario, idx) => (
                      <div key={idx} className="bg-white border-2 border-green-200 rounded-xl p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h4 className="text-lg font-bold text-gray-900 mb-2">{scenario.scenario_name}</h4>
                            <div className="flex items-center space-x-4 text-sm mb-3">
                              <div>
                                <span className="text-gray-600">Baseline:</span>
                                <span className="font-bold ml-1">{scenario.baseline_hours}h</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Optimized:</span>
                                <span className="font-bold text-green-600 ml-1">{scenario.optimized_hours}h</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Reduction:</span>
                                <span className="font-bold text-green-600 ml-1">-{scenario.reduction_hours}h ({scenario.total_reduction_pct}%)</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="text-sm font-semibold text-gray-900 mb-3">Applied Levers:</p>
                          <div className="space-y-2">
                            {scenario.applied_levers.map((lever, lidx) => (
                              <div key={lidx} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">{lever.lever}</span>
                                <span className="font-bold text-green-600">-{lever.impact_hours.toFixed(1)}h ({lever.reduction_pct.toFixed(0)}%)</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Go-Live Windows Tab */}
              {activeTab === 'windows' && (
                <div className="space-y-6">
                  {prediction.golive_window_recommendations.map((window) => (
                    <div 
                      key={window.window_id}
                      className={`border-2 rounded-xl p-6 ${
                        window.recommended ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-lg font-bold text-gray-900">Window #{window.window_id}</h4>
                            {window.recommended && (
                              <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                                RECOMMENDED
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">Start: {window.start_time}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-blue-600">{window.required_duration_hours}h</div>
                          <div className="text-xs text-gray-500">Required Window</div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 mb-3">
                        <p className="text-sm font-semibold text-gray-900 mb-1">Rationale:</p>
                        <p className="text-sm text-gray-700">{window.rationale}</p>
                      </div>

                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-sm text-gray-700">{window.business_impact}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* AI Narrative Tab */}
              {activeTab === 'narrative' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold flex items-center space-x-2">
                        <Sparkles className="w-6 h-6 text-blue-600" />
                        <span>Downtime Analysis Summary</span>
                      </h3>
                      {prediction.ai_narratives.generated_by.includes('GPT') && (
                        <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                          {prediction.ai_narratives.generated_by}
                        </span>
                      )}
                    </div>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">{prediction.ai_narratives.executive_summary}</p>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Key Recommendations</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <TrendingDown className="w-5 h-5 text-green-600 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          Best optimization: {prediction.summary.max_reduction_pct}% reduction possible through combined levers
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          Plan for {prediction.summary.p80_hours}h window (P80 conservative estimate)
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          Communicate P50 ({prediction.summary.p50_hours}h) internally, P80 ({prediction.summary.p80_hours}h) to business
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DowntimePredictor;
